import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { googleMapsLoaderOptions } from "../../utils/googleMapsLoader";
import "./GoogleMapDark.css";

// Dark style matching your palette
const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#23252B" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#23252B" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#bfc4d1" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#2b3a49" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#23252B" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#2b3a49" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#374959" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#bfc4d1" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#7495b4" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#23252B" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#bfc4d1" }] }
];

const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px',
  overflow: 'hidden',
};

export default function GoogleMapDark({ address, lat, lng }) {
  const { t } = useTranslation();
  // Don't try to load maps if we don't have a valid API key
  const { isLoaded } = useJsApiLoader(googleMapsLoaderOptions || { id: "google-maps-script", googleMapsApiKey: "" });
  const [center, setCenter] = useState({ lat: 52.1326, lng: 5.2913 }); // Default to center of NL
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Geocode address if no lat/lng provided
  useEffect(() => {
    // Only geocode if Maps API is loaded and we have an API key
    if (isLoaded && googleMapsLoaderOptions && address && !lat && !lng) {
      setIsGeocoding(true);
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          setCenter({
            lat: location.lat(),
            lng: location.lng()
          });
        } else {
          console.warn('Geocoding failed for address:', address, 'Status:', status);
          // Keep default center
        }
        setIsGeocoding(false);
      });
    } else if (lat && lng) {
      setCenter({ lat, lng });
    }
  }, [isLoaded, address, lat, lng]);

  // Handle marker click to open navigation
  const handleMarkerClick = () => {
    if (center.lat && center.lng) {
      // Check if user is on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        // Open in native maps app (works on both iOS and Android)
        window.open(`https://maps.google.com/maps?daddr=${center.lat},${center.lng}&amp;ll=`);
      } else {
        // Open in Google Maps web with directions
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}`);
      }
    } else if (address) {
      // Fallback to address if no coordinates
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`);
    }
  };

  // If no API key, show a message instead of the map
  if (!googleMapsLoaderOptions) {
    return (
      <div className="map-no-api-key">
        {t('map.noApiKey')}
      </div>
    );
  }

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={14}
      options={{
        styles: darkMapStyle,
        disableDefaultUI: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      }}
    >
      {!isGeocoding && (
        <Marker
          position={center}
          onClick={handleMarkerClick}
          title={t('map.clickToNavigate')}
          cursor="pointer"
        />
      )}
    </GoogleMap>
  ) : (
    <div className="map-loading">
      {t('map.loading')}
    </div>
  );
}

GoogleMapDark.propTypes = {
    address: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
};
